import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type { BulkActionJob, BulkActionStatus, BulkActionsState } from '../types/store/bulkActions.types'

const findJobByBackendId = (jobs: BulkActionJob[], backendId: string) =>
  jobs.find((j) => j.id === backendId) ??
  jobs.find((j) => j.meta?.exportTaskId === backendId) ??
  // Bulk traffic PDF exports track their backend row under this meta key, so
  // the backend's send_progress_push (keyed by the row id) can find the job.
  jobs.find((j) => j.meta?.trafficBulkExportId === backendId)

export const useBulkActionsStore = create<BulkActionsState>()(
  devtools(
    persist(
      (set) => ({
        bulkActions: [],
        isAutoResumeInProgress: false,

        addBulkActionJob: (job) =>
          set((state) => ({
            bulkActions: [...state.bulkActions, { ...job, createdAt: Date.now(), updatedAt: Date.now() }],
          })),

        updateBulkActionJob: (jobId, updates) =>
          set((state) => {
            const jobIndex = state.bulkActions.findIndex((j) => j.id === jobId)
            if (jobIndex === -1) return state

            const existingJob = state.bulkActions[jobIndex]

            // Deep equality check for meta to prevent unnecessary updates
            const metaChanged = updates.meta && JSON.stringify(updates.meta) !== JSON.stringify(existingJob.meta)

            // Check if the update would actually change anything significant
            const hasSignificantChange =
              (updates.status !== undefined && updates.status !== existingJob.status) ||
              (updates.processed !== undefined && updates.processed !== existingJob.processed) ||
              (updates.total !== undefined && updates.total !== existingJob.total) ||
              (updates.message !== undefined && updates.message !== existingJob.message) ||
              metaChanged

            if (!hasSignificantChange) {
              return state
            }

            const now = Date.now()
            const newBulkActions = [...state.bulkActions]
            newBulkActions[jobIndex] = { ...existingJob, ...updates, updatedAt: now }

            return { bulkActions: newBulkActions }
          }),

        updateProgressFromPush: ({ jobId, processed, total, status }) =>
          set((state) => {
            const match = findJobByBackendId(state.bulkActions, jobId)
            if (!match) return state
            const processedChanged = processed !== undefined && processed !== match.processed
            const totalChanged = total !== undefined && total !== match.total
            const statusChanged = status !== undefined && status !== match.status
            if (!processedChanged && !totalChanged && !statusChanged) return state
            const idx = state.bulkActions.indexOf(match)
            const newBulkActions = [...state.bulkActions]
            newBulkActions[idx] = {
              ...match,
              ...(processedChanged && { processed: processed as number }),
              ...(totalChanged && { total: total as number }),
              ...(statusChanged && { status: status as BulkActionStatus }),
              updatedAt: Date.now(),
            }
            return { bulkActions: newBulkActions }
          }),

        markComplete: (jobId, status) =>
          set((state) => {
            const match = findJobByBackendId(state.bulkActions, jobId)
            if (!match) return state
            const completedProcessed = match.total > 0 ? match.total : match.processed
            if (match.status === status && match.processed === completedProcessed) return state
            const idx = state.bulkActions.indexOf(match)
            const newBulkActions = [...state.bulkActions]
            newBulkActions[idx] = {
              ...match,
              status,
              processed: completedProcessed,
              updatedAt: Date.now(),
            }
            return { bulkActions: newBulkActions }
          }),

        removeBulkActionJob: (jobId) =>
          set((state) => ({ bulkActions: state.bulkActions.filter((j) => j.id !== jobId) })),

        clearCompletedBulkActions: () =>
          set((state) => ({
            bulkActions: state.bulkActions.filter((j) => j.status !== 'completed' && j.status !== 'cancelled'),
          })),

        setAutoResumeInProgress: (inProgress) => set({ isAutoResumeInProgress: inProgress }),
      }),
      {
        name: 'bulk-actions-storage',
        partialize: (state) => ({ bulkActions: state.bulkActions }),
      },
    ),
    { name: 'BulkActionsStore', enabled: import.meta.env.DEV },
  ),
)
